import { Box, OsCard, OsGrid, OsStack } from "@outschool/backpack";
import { ACTIVITY_LISTING_PHOTO_DATA_ATTRIBUTE } from "@outschool/ui-components-classroom";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import React, { useRef } from "react";

import { useIsActivitySavedByCurrentUser } from "../saving/graphql/hooks";
import HeaderPhoto from "../search/searchResultsGalleryExperiment/HeaderPhoto";
import ListingDetails from "../search/searchResultsGalleryExperiment/ListingDetails";
import { NextStartTime } from "../search/searchResultsGalleryExperiment/NextStartTime";
import PriceDetails from "../search/searchResultsGalleryExperiment/PriceDetails";
import TeacherInfo from "../search/searchResultsGalleryExperiment/TeacherInfo";
import Title from "../search/searchResultsGalleryExperiment/Title";
import TopBar from "../search/searchResultsGalleryExperiment/TopBar";
import { useMembershipRequirement } from "../subscriptions/hooks/useMembershipRequirement";
import { ActivityListingProps } from "./ActivityListing";

const CARD_WIDTH_PX = 320;

/**
 * Stub for the Search Results Gallery Experiment listing.
 *
 * TODO: When we launch this experiment, we should update the name of this component to something more descriptive,
 * like ActivityCard or similar.
 */
export default function SearchResultsGalleryExperimentListing({
  listing,
  showFundingBadge,
  trackingProperties,
  disabled,
  openInNewTab,
  listingIndex,
  isSelected,
  isComparing,
  isCheckboxDisabled,
  onSelectToCompare,
}: ActivityListingProps & {
  isComparing: boolean;
  isCheckboxDisabled: boolean;
  onSelectToCompare: () => void;
}) {
  const isActivitySavedByCurrentUser = useIsActivitySavedByCurrentUser(
    listing.activity,
  );
  const { showCredits } = useMembershipRequirement();
  const { activity, leader, insertionId, nextSection } = listing;

  const activityLinkRef = useRef<HTMLAnchorElement>(null);
  const listingCardRef = useRef<HTMLDivElement>(null);

  // Track activity *views* on the listing card. Clicks are tracked by the underlying Link in
  // Title.tsx.
  useImpressionTracking({
    node: listingCardRef.current,
    trackingLabel: "Activity Impression",
    uniqueId: activity?.uid,
    trackingEventName: "Activity Impression",
    impressionEventName: "Activity Impression View",
    trackViews: true,
    sharedProperties: {
      ...trackingProperties,
      activity_uid: listing.activity.uid,
      ...(insertionId ? { insertion_id: insertionId } : {}),
    },
  });

  return (
    // This is actually a grid item, not a grid container. SearchResultsGalleryExperiment renders a grid container with the items.
    // This must be an OsGrid so that the parent can control the grid item spacing.
    <OsGrid ref={listingCardRef} sx={{ width: `${CARD_WIDTH_PX}px` }}>
      <OsCard
        padding={2}
        outlined
        selected={isSelected}
        shadowVariant={"primary"}
        // Only set the link ref if we are not comparing, otherwise the link will be clicked when the checkbox is clicked.
        linkRef={isComparing ? undefined : activityLinkRef}
        onClick={() => {
          if (isComparing) {
            onSelectToCompare();
          }
        }}
      >
        <Box
          sx={{
            // Hack: Bootleg version of the interactiveEffect="zoom" prop on OsCardMedia.
            [`[${ACTIVITY_LISTING_PHOTO_DATA_ATTRIBUTE}]`]: {
              transition: "background-size 0.05s ease-in-out",
              backgroundSize: "100% !important",
            },
            [`&:hover [${ACTIVITY_LISTING_PHOTO_DATA_ATTRIBUTE}]`]: {
              backgroundSize: "110% !important",
            },
          }}
        >
          {/* 
          Note: using a stack to render these cards means that we need to ensure each element within the card has a consistent height across all cards.
          This is because we want corresponding elements to be aligned vertically with each other when rendered in a grid.
          */}
          <OsStack direction="column" spacing={3} fullWidth>
            <TopBar
              section={nextSection}
              activity={activity}
              trackingProperties={trackingProperties}
              isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
              disabled={disabled ?? false}
              leader={leader}
              isComparing={isComparing ?? false}
              isSelected={isSelected ?? false}
              isCheckboxDisabled={isCheckboxDisabled ?? false}
              onSelectToCompare={onSelectToCompare}
            />
            <HeaderPhoto activity={activity} listingIndex={listingIndex ?? 0} />
            <Title
              ref={activityLinkRef}
              activity={activity}
              leader={leader}
              disabled={disabled ?? false}
              openInNewTab={openInNewTab ?? false}
              listingIndex={listingIndex ?? 0}
              insertionId={insertionId}
              trackingProperties={trackingProperties}
            />
            <ListingDetails
              // @ts-expect-error TS(2322): Type '{ __typename?: "Activity" | undefined; uid: ... Remove this comment to see the full error message
              activity={activity}
              showCredits={showCredits}
              showFundingBadge={showFundingBadge}
            />
            <TeacherInfo leader={leader} />
            <PriceDetails
              // @ts-expect-error TS(2322): Type '{ __typename?: "Activity" | undefined; uid: ... Remove this comment to see the full error message
              activity={activity}
              showCredits={showCredits ?? false}
            />
            <NextStartTime
              nextSectionStartTime={nextSection?.start_time}
              nextOngoingMeetingStartTime={
                nextSection?.nextOngoingMeeting?.start_time
              }
            />
          </OsStack>
        </Box>
      </OsCard>
    </OsGrid>
  );
}

/**
 * Placeholder card to fill out rows.
 */
export function BlankSearchResultsGalleryExperimentListing() {
  return <OsGrid sx={{ width: `${CARD_WIDTH_PX}px` }} />;
}
